import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { productCategoryOptions } from "../mock/mockData";
import { useSettings } from "../context/SettingsContext";
import { submitQuote } from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Mail, Phone, MapPin, MessageCircle, Upload, Send, Building2, CheckCircle2, Loader2 } from "lucide-react";

const initialForm = {
  name: "", company: "", country: "", email: "", phone: "",
  productCategory: "", quantity: "", eventDate: "", message: "",
};

const Contact = () => {
  const location = useLocation();
  const prefill = location.state?.product;
  const { toast } = useToast();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    ...initialForm,
    message: prefill ? `I'm interested in: ${prefill}. Please send me a quotation.` : "",
  });
  const [fileName, setFileName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill required fields", description: "Name, email and message are required.", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await submitQuote({ ...form, fileName });
      setSubmitted(true);
      toast({ title: "Quote request sent!", description: "Our team will reply within 24 hours." });
      setForm(initialForm);
      setFileName("");
    } catch (err) {
      toast({ title: "Submission failed", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className="bg-ink-gradient py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#E7C766] font-bold text-sm uppercase tracking-widest">Contact & Quotation</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white mt-3">Request Your Custom Quote</h1>
          <p className="text-neutral-300 mt-4 max-w-2xl mx-auto">Tell us about your project and receive a factory-direct quotation within 24 hours.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-7 border border-neutral-100">
              <h3 className="font-display font-bold text-lg text-[#14171c] mb-5">Get In Touch</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#C9A227] mt-0.5" /><div><div className="text-neutral-500">Email</div><div className="font-semibold text-[#14171c]">{settings.email}</div></div></li>
                <li className="flex items-start gap-3"><Phone className="w-5 h-5 text-[#C9A227] mt-0.5" /><div><div className="text-neutral-500">Phone</div><div className="font-semibold text-[#14171c]">{settings.phone}</div></div></li>
                <li className="flex items-start gap-3"><MessageCircle className="w-5 h-5 text-[#C9A227] mt-0.5" /><div><div className="text-neutral-500">WhatsApp</div><div className="font-semibold text-[#14171c]">{settings.whatsapp}</div></div></li>
                <li className="flex items-start gap-3"><MapPin className="w-5 h-5 text-[#C9A227] mt-0.5" /><div><div className="text-neutral-500">Factory</div><div className="font-semibold text-[#14171c]">{settings.addressCN}</div></div></li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl p-7 border border-neutral-100">
              <h3 className="font-display font-bold text-lg text-[#14171c] mb-5 flex items-center gap-2"><Building2 className="w-5 h-5 text-[#C9A227]" /> Our Offices</h3>
              <ul className="space-y-3">
                {(settings.offices || []).map((o) => (
                  <li key={o.city} className="flex items-center justify-between"><span className="font-semibold text-[#14171c]">{o.city}</span><span className="text-xs text-neutral-500">{o.role}</span></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 border border-neutral-100">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-[#C9A227] mx-auto mb-4" />
                  <h3 className="font-display font-extrabold text-2xl text-[#14171c]">Thank You!</h3>
                  <p className="text-neutral-600 mt-2">Your quote request has been received. Our team will reply within 24 hours.</p>
                  <Button onClick={() => setSubmitted(false)} className="mt-6 bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full px-7">Send Another Request</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div><Label>Name *</Label><Input className="mt-1.5" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" /></div>
                    <div><Label>Company</Label><Input className="mt-1.5" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" /></div>
                    <div><Label>Country</Label><Input className="mt-1.5" value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="Country" /></div>
                    <div><Label>Email *</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" /></div>
                    <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+00 000 000 000" /></div>
                    <div>
                      <Label>Product Category</Label>
                      <Select value={form.productCategory} onValueChange={(v) => update("productCategory", v)}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>{productCategoryOptions.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Quantity</Label><Input className="mt-1.5" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} placeholder="e.g. 500 pcs" /></div>
                    <div><Label>Event Date</Label><Input type="date" className="mt-1.5" value={form.eventDate} onChange={(e) => update("eventDate", e.target.value)} /></div>
                  </div>
                  <div><Label>Message *</Label><Textarea className="mt-1.5" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project, colours, sizes and any references..." /></div>
                  <div>
                    <Label>Upload Artwork / Logo</Label>
                    <label className="mt-1.5 flex items-center gap-3 border border-dashed border-neutral-300 rounded-lg px-4 py-3 cursor-pointer hover:border-[#C9A227] transition-colors">
                      <Upload className="w-5 h-5 text-[#C9A227]" />
                      <span className="text-sm text-neutral-600">{fileName || "Choose a file (PNG, JPG, PDF, AI)"}</span>
                      <input type="file" className="hidden" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} />
                    </label>
                  </div>
                  <Button type="submit" disabled={sending} className="w-full bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 text-base font-semibold">
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Request a Quote
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
